import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import { useAuth } from '../context/AuthContext';
import ExpenseService from '../services/expenseService';
import { Expense } from '../types/Expense';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';
import { formatAmount as fmt } from '../utils/formatAmount';
import { thisWeek } from '../utils/dateRange';

interface Stats {
  totalAmount: number;
  totalCount: number;
  thisMonthAmount: number;
  thisMonthCount: number;
  thisWeekAmount: number;
  thisWeekCount: number;
}

function computeStats(expenses: Expense[], now = new Date()): Stats {
  const { start: weekStart, end: weekEnd } = thisWeek(now);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  const inThisMonth = expenses.filter(e => e.createdAt >= monthStart && e.createdAt <= monthEnd);
  const inThisWeek = expenses.filter(e => e.createdAt >= weekStart && e.createdAt <= weekEnd);

  return {
    totalAmount: expenses.reduce((sum, e) => sum + e.amount, 0),
    totalCount: expenses.length,
    thisMonthAmount: inThisMonth.reduce((sum, e) => sum + e.amount, 0),
    thisMonthCount: inThisMonth.length,
    thisWeekAmount: inThisWeek.reduce((sum, e) => sum + e.amount, 0),
    thisWeekCount: inThisWeek.length,
  };
}

const ExpenseSummaryCard: React.FC = () => {
  const { user } = useAuth();
  const { data: expenses, loading } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return ExpenseService.subscribeToAllExpenses(onUpdate, onError);
    },
    [] as Expense[],
  );
  const stats = useMemo(() => computeStats(expenses), [expenses]);
  const s = loading ? null : stats;

  return (
    <View style={styles.wrapper}>
      <LinearGradient
        colors={['#063669', '#274e82']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.card}
      >
        {s === null ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator testID="activity-indicator" size="small" color="white" />
          </View>
        ) : (
          <>
            <View style={styles.heroRow}>
              <View style={styles.heroLeft}>
                <Text style={styles.heroLabel}>TOTAL EXPENSES</Text>
                <Text style={styles.heroAmount}>{fmt(s.totalAmount)}</Text>
              </View>
              <View style={styles.badge}>
                <Text style={styles.badgeCount}>{s.totalCount}</Text>
                <Text style={styles.badgeLabel}>expenses</Text>
              </View>
            </View>

            <View style={styles.tilesRow}>
              <View style={styles.tile}>
                <Text style={styles.tileLabel}>This Month</Text>
                <Text style={styles.tileCount}>{s.thisMonthCount} expenses</Text>
                <Text style={styles.tileAmount}>{fmt(s.thisMonthAmount)}</Text>
              </View>
              <View style={[styles.tile, styles.weekTile]}>
                <Text style={styles.tileLabel}>This Week</Text>
                <Text style={styles.tileCount}>{s.thisWeekCount} expenses</Text>
                <Text style={styles.tileAmount}>{fmt(s.thisWeekAmount)}</Text>
              </View>
            </View>
          </>
        )}
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 20,
  },
  card: {
    borderRadius: 6,
    marginBottom: 12,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroLeft: { flex: 1 },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
  },
  badge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  badgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  badgeLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  tile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  weekTile: {
    borderWidth: 1,
    borderColor: 'rgba(255,180,100,0.4)',
  },
  tileLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileCount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    color: 'white',
    lineHeight: 20,
  },
  tileAmount: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
});

export default ExpenseSummaryCard;
